<template>
  <scroll-into-view class="inline-flex gap-1 text-sm overflow-x-auto max-w-full pb-2 sm:text-base sm:gap-2 lg:pb-0">
    <template v-for="(tab, index) in tabs">
      <a
        v-if="tab.url"
        :key="`link_${index}`"
        :href="tab.url"
        :class="[
          tabClasses.base,
          tabClasses[tab.active ? 'active' : 'inactive'],
        ]"
      >
        {{ tab.title || tab.name }}
      </a>
      <button
        v-else
        :key="`button_${index}`"
        type="button"
        :class="[
          tabClasses.base,
          tabClasses[activeTab == tab.id ? 'active' : 'inactive'],
        ]"
        @click="changeTab(tab.id)"
      >
        {{ tab.title }}
      </button>
    </template>
  </scroll-into-view>
</template>

<script setup>
import ScrollIntoView from "@/components/ScrollIntoView.vue"
import { computed, onMounted, ref } from "vue"

const props = defineProps({
  tabs: Array,
  color: { type: String, default: "orange" },
})

const activeTab = ref()

const tabClasses = computed(() => {
  return {
    base: [
      "cursor-pointer block border font-medium rounded-md py-1.5 px-3 ring-inset whitespace-nowrap sm:py-2 sm:px-4",
      "focus-visible:border-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:z-10",
      "transition-colors",
    ].join(" "),
    active: props.color == "blue"
      ? "active border-blue-100 dark:border-blue-500/20 bg-blue-50 dark:bg-blue-700/15 shadow-sm text-blue-700 dark:text-blue-500 hover:text-blue-500 dark:hover:text-blue-500 dark:hover:border-blue-500/50"
      : "active border-orange-100 dark:border-orange-500/20 bg-orange-50 dark:bg-orange-700/15 shadow-sm text-orange-700 dark:text-orange-600 hover:text-orange-700 dark:hover:text-orange-500 dark:hover:border-orange-500/50",
    inactive: props.color == "blue"
      ? "border-transparent text-inherit dark:text-gray-300 hover:text-blue-700 dark:hover:text-blue-500"
      : "border-transparent text-inherit dark:text-gray-300 hover:text-orange-700 dark:hover:text-orange-500",
  }
})

const changeTab = (tabId) => {
  activeTab.value = tabId
  // Scroll to the selected section if needed
  const el = document.getElementById(tabId)

  if (el) {
    el.scrollIntoView({ behavior: "smooth" })
  }
}

const handleIntersection = (entries) => {
  for (const entry of entries) {
    if (entry.isIntersecting) {
      activeTab.value = entry.target.id
    }
  }
}

onMounted(() => {
  const observer = new IntersectionObserver(handleIntersection, {
    root: null,
    rootMargin: "20px",
    threshold: 0.3, // Adjust this value to determine when a section is in view
  })

  props.tabs.forEach((tab) => {
    if (tab.id) {
      const el = document.getElementById(tab.id)

      if (el) {
        observer.observe(el)
      }
    }
  })
})
</script>
